import { useLayoutEffect, useMemo, useState } from 'react';
import { Lookup } from 'utils/types';

export enum MediaSize {
    xxlarge = 'xxlarge',
    xlarge = 'xlarge',
    large = 'large',
    medium = 'medium',
    small = 'small',
    xsmall = 'xsmall',
}

export enum MediaSizeBreakpoint {
    small = 520,
    medium = 768,
    large = 1024,
    xlarge = 1280,
    xxlarge = 1600,
}

export const MediaQueries = {
    /**
     * Targets small viewport and below.
     *
     * Usage: `@media ${MediaQueries.smallAndDown} { ... }`
     */
    smallAndDown: `(max-width: ${MediaSizeBreakpoint.small}px)`,
    /**
     * Targets small viewports and up.
     *
     * Usage: `@media ${MediaQueries.smallAndUp} { ... }`
     */
    smallAndUp: `(min-width: ${MediaSizeBreakpoint.small}px)`,
    /**
     * Targets medium viewports and down.
     *
     * Usage: `@media ${MediaQueries.mediumAndDown} { ... }`
     */
    mediumAndDown: `(max-width: ${MediaSizeBreakpoint.medium}px)`,
    /**
     * Targets medium viewports and up.
     *
     * Usage: `@media ${MediaQueries.mediumAndUp} { ... }`
     */
    mediumAndUp: `(min-width: ${MediaSizeBreakpoint.medium}px)`,
    /**
     * Targets large viewports and up.
     *
     * Usage: `@media ${MediaQueries.largeAndUp} { ... }`
     */
    largeAndDown: `(max-width: ${MediaSizeBreakpoint.large}px)`,
    /**
     * Targets large viewports and up.
     *
     * Usage: `@media ${MediaQueries.largeAndUp} { ... }`
     */
    largeAndUp: `(min-width: ${MediaSizeBreakpoint.large}px)`,
    /**
     * Targets large viewports and up.
     *
     * Usage: `@media ${MediaQueries.largeAndUp} { ... }`
     */
    xlargeAndDown: `(max-width: ${MediaSizeBreakpoint.xlarge}px)`,
    /**
     * Targets extra large viewports and up.
     *
     * Usage: `@media ${MediaQueries.xlargeAndUp} { ... }`
     */
    xlargeAndUp: `(min-width: ${MediaSizeBreakpoint.xlarge}px)`,
    /**
     * Targets large viewports and up.
     *
     * Usage: `@media ${MediaQueries.largeAndUp} { ... }`
     */
    xxlargeAndDown: `(max-width: ${MediaSizeBreakpoint.xxlarge}px)`,
    /**
     * Targets extra extra large viewports and up.
     *
     * Usage: `@media ${MediaQueries.xxlargeAndUp} { ... }`
     */
    xxlargeAndUp: `(min-width: ${MediaSizeBreakpoint.xxlarge}px)`,
};

export const getMediaSizeFromViewportWidth = (viewportWidth: number) => {
    if (viewportWidth < MediaSizeBreakpoint.small) {
        return MediaSize.xsmall;
    }
    if (
        viewportWidth >= MediaSizeBreakpoint.small &&
        viewportWidth < MediaSizeBreakpoint.medium
    ) {
        return MediaSize.small;
    }
    if (
        viewportWidth >= MediaSizeBreakpoint.medium &&
        viewportWidth < MediaSizeBreakpoint.large
    ) {
        return MediaSize.medium;
    }
    if (
        viewportWidth >= MediaSizeBreakpoint.large &&
        viewportWidth < MediaSizeBreakpoint.xlarge
    ) {
        return MediaSize.large;
    }

    if (
        viewportWidth >= MediaSizeBreakpoint.xlarge &&
        viewportWidth < MediaSizeBreakpoint.xxlarge
    ) {
        return MediaSize.xlarge;
    }

    return MediaSize.xxlarge;
};

export function useWindowSize(hookHeight?: boolean) {
    const [width, setWidth] = useState(window.innerWidth);
    const [_height, setHeight] = useState(window.innerHeight);

    useLayoutEffect(() => {
        // The handle to the next resize width callback, if any.
        let handle: number | undefined;

        // Actually emits the current width to the state, which will cause
        // components to rerender.
        function dispatchWidth() {
            setWidth(window.innerWidth);
            if (hookHeight) {
                setHeight(window.innerHeight);
            }
        }

        // Handle resize is called when the resize event occurs. It will debounce the
        // width dispatch so that width changes occur no sooner than the next time
        // the frame is painted by the browser.
        function handleResize() {
            window.clearTimeout(handle);
            handle = requestAnimationFrame(dispatchWidth);
        }

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [hookHeight]); // This should only run on mount and unmount

    return useMemo(() => getMediaSizeFromViewportWidth(width), [width]);
}

/**
 * Returns true if the viewport is XS, S, or M. Returns false if it is L, XL, XXL.
 */
export function isViewportAtMostMedium(size: MediaSize) {
    return (
        size === MediaSize.xsmall || size === MediaSize.small || size === MediaSize.medium
    );
}

/**
 * Returns true if the viewport is L, XL, XXL. Returns false if it is XS, S, or M.
 * This function is the opposite of `isViewportAtMostMedium`.
 */
export function isViewportAtLeastLarge(size: MediaSize) {
    return (
        size === MediaSize.large ||
        size === MediaSize.xlarge ||
        size === MediaSize.xxlarge
    );
}

/**
 * Returns true if the viewport is at least a SM viewport. Returns false if it is XS.
 */
export function isViewportAtLeastSmall(size: MediaSize) {
    return (
        size === MediaSize.small ||
        size === MediaSize.medium ||
        size === MediaSize.large ||
        size === MediaSize.xlarge ||
        size === MediaSize.xxlarge
    );
}

const ordinalValues: Lookup<MediaSize, number> = {
    [MediaSize.xsmall]: 1,
    [MediaSize.small]: 2,
    [MediaSize.medium]: 3,
    [MediaSize.large]: 4,
    [MediaSize.xlarge]: 5,
    [MediaSize.xxlarge]: 6,
};

/**
 * Returns if the current viewport is not smaller than the supplied size.
 */
export function useIsViewportAtLeast(size: MediaSize): boolean {
    return ordinalValues[useWindowSize()] >= ordinalValues[size];
}

/**
 * Returns if the current viewport is not larger than the supplied size.
 */
export function useIsViewportAtMost(size: MediaSize): boolean {
    return ordinalValues[useWindowSize()] <= ordinalValues[size];
}
